"use client"

import { buildExcerpt } from '@/lib/blog-excerpt'

import Link from "next/link"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { hubBreadcrumbs } from "@/lib/breadcrumbs"
import { ArticleCard } from "@/components/brand-kit/article-card"
import { ToolLinkCard } from "@/components/brand-kit/tool-link-card"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { ProductCarouselCard } from "@/components/brand-kit/product-carousel-card"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { OptimizedImg } from "@/components/ui/optimized-img"
import {
  PAGE_W,
  transformProducts, toCarouselProduct,
  getRatedFirstProducts,
  formatDate, resolveFeatureEmoji, resolveConsiderationHref,
  PageSectionHeader, ScrollCarousel,
  hubHeroIntro,
  type HubClientProps,
  hubCollectionHref,
  hubBrandHref,
  MaybeLink,
} from "./hub-shared"

export default function GuideHub({ hub, products, articles, relatedHubs = [] }: HubClientProps) {
  const collectionSlug = hub.collection_slug || hub.slug
  const collectionHref = hubCollectionHref(hub) // FRIDGE-1119: null when the collection does not render
  const displayProducts = transformProducts(products)
  const editorPicks = getRatedFirstProducts(displayProducts, 8)

  return (
    <div>
      <div className={`${PAGE_W} py-8`}>
        {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same hubBreadcrumbs(hub) as the BreadcrumbList (FRIDGE-269) */}
        <Breadcrumb trail={hubBreadcrumbs(hub)} />

        {/* Hero Banner — Buying Guide (text-only) */}
        <section className="mb-12 rounded-2xl overflow-hidden border border-gray-100 bg-gradient-to-br from-white to-gray-50 shadow-[0_4px_24px_rgba(0,25,69,0.06)]">
          <div className="py-12 px-8 lg:px-12">
            <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">Buying Guide</span>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">{hub.title}</h1>
            {/* First sentence — keyword-placement standard (plan item 15).
                Always renders (fallback leads with hub.title). */}
            <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-lg">{hubHeroIntro(hub)}</p>
            {hub.slug === 'refrigerator-buying-guide' && (
              <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-lg">
                Start by <Link href="/tools/kitchen-space-planner" className="text-[#0469f0] underline">checking your kitchen opening and clearances</Link>, then compare refrigerator styles that fit.
              </p>
            )}
            {/* FRIDGE-1483: the sizing guide sends shoppers to the base width pages,
                the answer pages for the width queries. */}
            {hub.slug === 'refrigerator-sizing-guide' && (
              <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-lg">
                Browse refrigerators by recorded width:{' '}
                {[24, 28, 30, 32, 33, 35, 36].map((w, i) => (
                  <span key={w}>
                    {i > 0 ? ', ' : ''}
                    <Link href={`/collections/${w}-inch-wide-refrigerators`} className="text-[#0469f0] underline">{w}-inch wide</Link>
                  </span>
                ))}
                .
              </p>
            )}
            <button
              onClick={() => {
                const el = document.getElementById('guide-considerations')
                el?.scrollIntoView({ behavior: 'smooth' })
              }}
              className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              Explore the Guide
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
            </button>
          </div>
        </section>

        <div className="mb-6">
          <ProductShareButtons productTitle={hub.title} pageType="guide" variant="top" />
        </div>

        {/* ─── Key Considerations — redesigned cards with tool links ───── */}
        {hub.features && hub.features.length > 0 && (
          <section id="guide-considerations" className="mb-12">
            <PageSectionHeader eyebrow="What to Look For" title="Key Considerations" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hub.features.filter((f) => resolveConsiderationHref(f.title, relatedHubs, hub.tools_section || []) !== null).map((feature, i) => {
                const match = resolveConsiderationHref(feature.title, relatedHubs, hub.tools_section || [])!
                return (
                  <Link key={i} href={match.href} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] hover:border-[#0469f0]/20 transition-all flex no-underline">
                    <div className="w-1.5 bg-[#0469f0] flex-shrink-0" />
                    <div className="p-5 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-[#0469f0]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-base">{resolveFeatureEmoji(feature.icon)}</span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm">{feature.title}</h3>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed flex-grow">{match.href === '/tools/energy-cost-calculator' ? 'Estimate annual operating costs from the model’s EnergyGuide kWh and your electricity rate.' : feature.description}</p>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0469f0]">
                        {match.label}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </section>
        )}

        {/* ─── Explore Related Categories ─────────────────────────────── */}
        {relatedHubs.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Discover More" title="Explore Related Categories" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedHubs.slice(0, 5).map((rh) => (
                <ToolLinkCard key={rh.slug} title={rh.title} description="Browse collection" href={`/${rh.slug}`} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ─── FAQ — prominent in guides ────────────────────────────────── */}
      {hub.faqs && hub.faqs.length > 0 && (
        <div className="bg-gray-50 py-12">
          <div className={PAGE_W}>
            <PageSectionHeader eyebrow="Expert Answers" title="Frequently Asked Questions" />
            <FAQAccordion items={hub.faqs} defaultOpen={[0, 1]} columns={1} title="" bordered={false} className="mt-0" />
          </div>
        </div>
      )}

      {/* ─── Editor's Picks — single carousel ────────────────────────── */}
      {editorPicks.length > 0 && (
        <ScrollCarousel eyebrow="Our Recommendations" title="Editor's Picks" bgClass="bg-white">
          {editorPicks.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      {/* ─── Share Buttons (Bottom) ─────────────────────────────────── */}
      <div className={`${PAGE_W} py-4`}>
        <ProductShareButtons productTitle={hub.title} pageType="guide" variant="bottom" />
      </div>

      <div className={PAGE_W}>
        {/* ─── Tools — prominent in guides ──────────────────────────── */}
        {hub.tools_section && hub.tools_section.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Interactive Tools" title="Tools to Help You Decide" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {hub.tools_section.map((tool) => (
                <ToolLinkCard key={tool.href} title={tool.title} description={tool.description} href={tool.href} />
              ))}
            </div>
          </section>
        )}

        {/* ─── Related Reading ──────────────────────────────────────── */}
        {articles.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader
              eyebrow="Related Reading"
              title="Articles & Resources"
              actions={
                <Link href="/blogs/news" className="text-sm font-medium text-[#0469f0] hover:underline flex items-center gap-1">
                  All Articles
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} imageUrl={article.image || undefined} title={article.title} author={article.author_name || "Fridge.com"} authorImageUrl={article.authorImageUrl ?? null} date={formatDate(article.published_at)} excerpt={buildExcerpt(article.content || '', article.title)} href={`/blogs/news/${article.slug}`} />
              ))}
            </div>
          </section>
        )}

        {/* ─── CTA Banner ──────────────────────────────────────────── */}
        {hub.cta_title && (
          <section className="relative rounded-2xl overflow-hidden mb-12">
            <OptimizedImg src="https://images.unsplash.com/photo-1541604193435-22287d32c2c2?w=1200&q=80" alt="" fill sizes="(min-width: 1280px) 1216px, 100vw" className="object-cover" />
            <div className="absolute inset-0 bg-black/65" />
            <div className="relative z-10 text-center py-16 px-10">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3 drop-shadow-lg">{hub.cta_title}</h2>
              {hub.cta_description && <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">{hub.cta_description}</p>}
              {(hub.cta_button_link || collectionHref) ? (<Link href={(hub.cta_button_link || collectionHref) as string} className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#f08030] text-[var(--color-brand-action-text)] font-semibold px-7 py-3 rounded-lg transition-colors text-sm shadow-lg">
                {hub.cta_button_text || "Start Shopping"}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>) : null}
            </div>
          </section>
        )}

      </div>
    </div>
  )
}
